import { useEffect, useMemo, useRef, type MutableRefObject } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import { canRenderAsRichText } from './canRenderAsRichText';
import { fromEditorHtml, toEditorHtml } from './placeholderMarkup';
import { DEFAULT_RICH_TEXT_CONFIG, richTextExtensions, type RichTextConfig } from './richTextExtensions';
import { RichTextToolbar } from './RichTextToolbar';

export interface RichTextEditorProps {
    id: string;
    ariaLabel: string;
    value: string;
    onChange: (html: string) => void;
    onBlur?: () => void;
    disabled?: boolean;
    invalid?: boolean;
    errorId?: string;
    // Must be a stable, module-level reference: the editor's extensions are built once per config.
    config?: RichTextConfig;
    // Called instead of loading an external value the editor would alter on render.
    onUnsupportedValue?: () => void;
    insertRef?: MutableRefObject<((content: string) => void) | null>;
}

const SURFACE_CLASSES = [
    'min-h-[20rem] p-3 focus:outline-none',
    '[&_h2]:text-xl [&_h2]:font-semibold [&_h3]:text-lg [&_h3]:font-semibold',
    '[&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_a]:underline [&_p]:my-2',
].join(' ');

export function RichTextEditor({
    id,
    ariaLabel,
    value,
    onChange,
    onBlur,
    disabled = false,
    invalid = false,
    errorId,
    config = DEFAULT_RICH_TEXT_CONFIG,
    onUnsupportedValue,
    insertRef,
}: RichTextEditorProps) {
    const lastSynced = useRef(value);
    const initialContent = useRef(value);
    const editorProps = useMemo(
        () => ({
            attributes: {
                id,
                role: 'textbox',
                'aria-multiline': 'true',
                'aria-label': ariaLabel,
                'aria-invalid': invalid ? 'true' : 'false',
                ...(errorId ? { 'aria-describedby': errorId } : {}),
                class: SURFACE_CLASSES,
            },
        }),
        [id, ariaLabel, invalid, errorId],
    );
    const editor = useEditor({
        extensions: richTextExtensions(config),
        content: toEditorHtml(initialContent.current, config),
        editable: !disabled,
        editorProps,
        onUpdate: ({ editor: current }) => {
            lastSynced.current = current.isEmpty ? '' : fromEditorHtml(current.getHTML(), config);
            onChange(lastSynced.current);
        },
        onBlur: () => onBlur?.(),
    });

    useEffect(() => {
        editor.setEditable(!disabled, false);
    }, [editor, disabled]);

    useEffect(() => {
        if (value === lastSynced.current) return;
        lastSynced.current = value;
        if (onUnsupportedValue && !canRenderAsRichText(value, config)) {
            onUnsupportedValue();
            return;
        }
        editor.commands.setContent(toEditorHtml(value, config), { emitUpdate: false });
    }, [editor, value, config, onUnsupportedValue]);

    useEffect(() => {
        if (!insertRef) return undefined;
        insertRef.current = (content) => {
            editor.chain().focus().insertContent(toEditorHtml(content, config)).run();
        };
        return () => {
            insertRef.current = null;
        };
    }, [editor, insertRef, config]);

    return (
        <div className="rounded-lg border border-mp-input bg-mp-surface">
            {!disabled && <RichTextToolbar editor={editor} config={config} />}
            <EditorContent editor={editor} />
        </div>
    );
}
